import { useCallback, useEffect, useState } from 'react';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setRepeatAction } from 'app/store/AudioPlayer/slice';
import {
  useAudioPlayer,
  useFinishEvent,
  usePlayerEvents,
  useTimer,
  WaveSurferInstance,
} from '../hooks';
import { AudioPlayerControlsComponent } from './AudioPlayerControlsComponent';

export interface AudioPlayerControlsProps {
  isLoading: boolean;
  isPlaying: boolean;
  src?: string;
  isError?: boolean;
  isPlaylistMode: boolean;
  player?: WaveSurferInstance;
  isOpen: boolean;
  setIsPlayerReady: (isReady: boolean) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  isPlayerExpandable: boolean;
}

const SKIP_BACK_THRESHOLD_SEC = 3;

export const AudioPlayerControls = ({
  isLoading,
  isPlaying,
  src,
  isError,
  isPlaylistMode,
  player,
  isOpen,
  setIsPlayerReady,
  setIsPlaying,
  isPlayerExpandable,
}: AudioPlayerControlsProps) => {
  const dispatch = useAppDispatch();
  const { repeat } = useAppSelector(getAudioPlayer);
  const [loop, setLoop] = useState(repeat);
  const { skip, skipBack, playPauseAudio } = useAudioPlayer();
  const { queue } = useAppSelector(getAudioPlayer);

  useFinishEvent({ player, loop, setIsPlaying, skip });

  const { currentTime, setCurrentTime } = useTimer({
    player,
    isFetchingAudioSrc: isLoading,
    isOpen,
    shouldPlay: isPlaying,
  });

  const { duration } = usePlayerEvents(
    player,
    { isOpen, setIsPlayerReady },
    setCurrentTime
  );

  const shouldSkipBack =
    isPlaylistMode && !loop && currentTime < SKIP_BACK_THRESHOLD_SEC;

  const handleRewind = useCallback(() => {
    if (shouldSkipBack) {
      skipBack();
    }
    player?.seekTo(0);
  }, [player, shouldSkipBack, skipBack]);

  const toggleLoop = useCallback(() => {
    dispatch(setRepeatAction(!loop));
    setLoop(!loop);
  }, [dispatch, loop]);

  const handleSkip = useCallback(() => {
    if (loop) {
      player?.seekTo(0);
      return;
    }
    skip();
  }, [loop, player, skip]);

  useEffect(() => {
    navigator.mediaSession?.setActionHandler('play', () => {
      setIsPlaying(true);
      player?.play();
    });
    navigator.mediaSession?.setActionHandler('pause', () => {
      setIsPlaying(false);
      player?.pause();
    });
  }, [player, setIsPlaying]);

  useEffect(() => {
    navigator.mediaSession?.setActionHandler('previoustrack', () => {
      handleRewind();
    });
  }, [handleRewind]);

  useEffect(() => {
    navigator.mediaSession?.setActionHandler('nexttrack', () => {
      handleSkip();
    });
  }, [handleSkip]);

  useEffect(() => {
    if (isLoading) {
      setCurrentTime(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (isPlaying) {
      player?.play();
    } else {
      player?.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  return (
    <AudioPlayerControlsComponent
      src={src}
      currentTime={currentTime}
      duration={duration}
      loop={loop}
      isLoading={isLoading}
      isPlaying={isPlaying}
      isPlaylistMode={isPlaylistMode}
      isError={isError}
      isPlayerExpandable={isPlayerExpandable}
      handleRewind={handleRewind}
      handleSkip={queue.length > 1 ? handleSkip : undefined}
      onPlayPause={playPauseAudio}
      toggleLoop={toggleLoop}
    />
  );
};
